import { useEffect, type RefObject } from "react";
import { openGlobalSearch } from "@/lib/global-search";

/**
 * ⌘/Ctrl-K while the wall is interactive (Part 09 decision 2): pressed
 * anywhere in the window it focuses and selects the wall's search instead of
 * opening the search hub, whose own listener sits on the document, after
 * this capture listener. Pressed again with the field focused, it opens the
 * hub with the field's text, so Settings and the hub stay one key away.
 * Under a preview or Focus (`enabled` false) the hub opens as before, and
 * from a dialog or window surface outside the wall (the hub, the summoned
 * chat, marked `data-own-shortcuts`) ⌘K keeps its own meaning.
 */
const OWN_SHORTCUTS_SURFACE = "[role='dialog'], [data-own-shortcuts='true']";

export function useWallSearchShortcut(
  enabled: boolean,
  rootRef: RefObject<HTMLElement | null>,
  searchRef: RefObject<HTMLInputElement | null>,
  projectId: string,
) {
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key !== "k") return;
      const { target } = event;
      const inOtherSurface =
        target instanceof Element &&
        !!target.closest(OWN_SHORTCUTS_SURFACE) &&
        !rootRef.current?.contains(target);
      const search = searchRef.current;
      if (!search || inOtherSurface) return;
      event.preventDefault();
      event.stopPropagation();
      if (document.activeElement === search) {
        openGlobalSearch({
          chip: "all",
          projectId,
          query: search.value.trim(),
        });
        return;
      }
      search.focus();
      search.select();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [enabled, projectId, rootRef, searchRef]);
}
